import { useState, useEffect } from 'react';
import { Modal, PasswordInput, Button, Stack, Alert, Group } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { authService } from '../../services/authService';
import { extractErrorMessage } from '../../utils/errorUtils';

interface ChangePasswordModalProps {
  /** モーダルの開閉状態 */
  opened: boolean;
  /** 閉じるコールバック */
  onClose: () => void;
}

/** Firebase Auth の最小パスワード長 */
const MIN_PASSWORD_LENGTH = 6;

/**
 * パスワード変更モーダル
 *
 * ログイン中ユーザーが現在のパスワードと新しいパスワードを入力して変更する。
 * Firebase Auth の `reauthenticateWithCredential` + `updatePassword` を利用する。
 *
 * @param opened - モーダルの開閉状態
 * @param onClose - 閉じるコールバック
 */
export function ChangePasswordModal({ opened, onClose }: ChangePasswordModalProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!opened) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setValidationError(null);
      setSubmitError(null);
      setLoading(false);
    }
  }, [opened]);

  const validate = (): boolean => {
    setValidationError(null);

    if (!currentPassword) {
      setValidationError('現在のパスワードを入力してください');
      return false;
    }
    if (!newPassword) {
      setValidationError('新しいパスワードを入力してください');
      return false;
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setValidationError(`新しいパスワードは ${MIN_PASSWORD_LENGTH} 文字以上で入力してください`);
      return false;
    }
    if (newPassword !== confirmPassword) {
      setValidationError('新しいパスワードと確認用パスワードが一致しません');
      return false;
    }
    if (newPassword === currentPassword) {
      setValidationError('新しいパスワードは現在のパスワードと異なる値を設定してください');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setSubmitError(null);
    try {
      await authService.changePassword(currentPassword, newPassword);
      notifications.show({
        title: 'パスワードを変更しました',
        message: '次回ログインから新しいパスワードをご利用ください',
        color: 'green',
      });
      onClose();
    } catch (error: unknown) {
      setSubmitError(extractErrorMessage(error, 'パスワードの変更に失敗しました'));
    } finally {
      setLoading(false);
    }
  };

  const displayError = validationError || submitError;

  return (
    <Modal
      opened={opened}
      onClose={() => {
        if (loading) return;
        onClose();
      }}
      title="パスワード変更"
      centered
      closeOnEscape={!loading}
      closeOnClickOutside={!loading}
      withCloseButton={!loading}
    >
      <form onSubmit={handleSubmit} noValidate>
        <Stack gap="md">
          {displayError && (
            <Alert color="red" title="エラー">
              {displayError}
            </Alert>
          )}

          <PasswordInput
            label="現在のパスワード"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.currentTarget.value)}
            required
            disabled={loading}
          />

          <PasswordInput
            label="新しいパスワード"
            description={`${MIN_PASSWORD_LENGTH} 文字以上`}
            value={newPassword}
            onChange={(e) => setNewPassword(e.currentTarget.value)}
            required
            disabled={loading}
          />

          <PasswordInput
            label="新しいパスワード（確認）"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.currentTarget.value)}
            required
            disabled={loading}
          />

          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="subtle" onClick={onClose} disabled={loading}>
              キャンセル
            </Button>
            <Button type="submit" loading={loading}>
              変更する
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
